/**
 * Videos de YouTube en el programa: se reproducen desde YouTube, sin descargarlos. YouTube decide si
 * pone anuncios; con una cuenta de YouTube Premium abierta en la app KEORA Multimedia no salen.
 */

/** Id de un video de YouTube a partir de su enlace (watch, youtu.be, shorts, embed, live) o del id mismo. */
export function youtubeId(input: string): string | null {
  const v = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(v)) return v;
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^(www|m|music)\./, '');
  let id: string | null = null;
  if (host === 'youtu.be') id = url.pathname.split('/')[1] ?? null;
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') id = url.searchParams.get('v') ?? /^\/(?:shorts|embed|live|v)\/([^/?#]+)/.exec(url.pathname)?.[1] ?? null;
  return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
}

export const youtubeThumb = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

/** Reproductor para la pantalla: empieza solo, sin videos sugeridos al final ni anotaciones. */
export const youtubeEmbed = (id: string) => `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&iv_load_policy=3&playsinline=1&modestbranding=1`;

/** Título del video (sin clave de YouTube: el servicio oEmbed). Si no se puede, null. */
export async function youtubeTitle(id: string): Promise<string | null> {
  try {
    const r = await fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(`https://www.youtube.com/watch?v=${id}`)}`);
    if (!r.ok) return null;
    const data = (await r.json()) as { title?: string };
    return data.title?.trim() || null;
  } catch {
    return null;
  }
}

export type RecentVideo = { id: string; title: string };
const RECENT = 'keora.consola.youtube';

/** Los últimos videos de YouTube que se usaron en esta computadora. */
export function loadRecent(): RecentVideo[] {
  try {
    return (JSON.parse(localStorage.getItem(RECENT) ?? '[]') as RecentVideo[]).filter((v) => youtubeId(v.id) === v.id).slice(0, 30);
  } catch {
    return [];
  }
}

export function saveRecent(list: RecentVideo[]) {
  try {
    localStorage.setItem(RECENT, JSON.stringify(list.slice(0, 30)));
  } catch {
    // Sin almacenamiento: solo no se recuerda.
  }
}

interface YoutubeApi {
  /** Abre la ventana para iniciar sesión en YouTube (con la cuenta de la iglesia). Desde la versión 1.4.0 de la app. */
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /** ¿Hay sesión abierta en YouTube en esta computadora? */
  signedIn: () => Promise<boolean>;
}

export const youtubeApi = (): YoutubeApi | null => (typeof window !== 'undefined' ? ((window as Window & { keoraApp?: { youtube?: YoutubeApi } }).keoraApp?.youtube ?? null) : null);
